/**
 * Assessment Performance — async thunk for the metrics endpoint.
 *
 *   GET /accounts/{currentAccountUuid}/assessment-performance/metrics
 *
 * The Account multi-picker uses `useFetchList` against the
 * existing `/accounts/{uuid}/account-lookups` endpoint and
 * doesn't go through this file — same pattern as the
 * Population Health and AccountMultiPicker pickers.
 *
 * The drilldown modal does NOT have its own endpoint either —
 * `/metrics` already returns every dictionary metric for every
 * selected account, so the drilldown reads its row from the
 * in-memory comparison rows. See `useAccountBreakdown` in
 * `entities/assessment-performance/model/hooks.js`.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@shared/api/client';
import { buildAssessmentMetricsMock } from './assessmentPerformance.mock';

// Live endpoint as of 2026-04-27 — backend handed off
// `feature/assessment-performance-backend` and the response
// shape is contract-tested against `assessmentPerformance.mock.js`.
// Flip back to `true` only if the backend is unreachable during
// offline dev work; the mock factory still mirrors the live shape.
const USE_MOCKS = false;

const MOCK_DELAY_MS = 280;
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const withMock = async (mockFactory, liveCall) => {
  if (USE_MOCKS) {
    await sleep(MOCK_DELAY_MS);
    return { ok: true, data: mockFactory() };
  }
  return liveCall();
};

const buildQueryString = params => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    search.append(k, Array.isArray(v) ? v.join(',') : v);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Returns metric totals per child account for two periods in one
 * call. Used by the comparison table and both chart variants.
 *
 * `accountUuids` is required server-side — the page won't fire
 * the call until the user has picked at least one account in the
 * filter chip.
 */
export const fetchAssessmentMetrics = createAsyncThunk(
  'assessmentPerformance/fetchMetrics',
  async (
    { accountUuid, periodA, periodB, accountUuids = [], includeAutomated = false },
    { rejectWithValue }
  ) => {
    try {
      const queryString = buildQueryString({
        periodAStart: periodA?.start,
        periodAEnd: periodA?.end,
        periodBStart: periodB?.start,
        periodBEnd: periodB?.end,
        accountUuids: accountUuids.length ? accountUuids : undefined,
        // Absent and `false` mean the same to the API, but sending it
        // explicitly keeps the request self-describing in the network
        // tab — which matters here, because the default is the
        // non-obvious one (automated assessments are left out).
        includeAutomated,
      });
      const response = await withMock(
        () =>
          buildAssessmentMetricsMock({
            accountUuid,
            periodA,
            periodB,
            accountUuids,
            includeAutomated,
          }),
        () => client.get(`/accounts/${accountUuid}/survey-performance/metrics${queryString}`)
      );
      if (response.ok) {
        // Live QHS responses come wrapped as `{ data: <body>,
        // correlationUUID }`; the mock factory returns the body
        // directly. `response.data?.data ?? response.data` handles
        // both without a separate code path.
        const body = response.data?.data ?? response.data;
        return {
          ok: true,
          data: {
            rows: body.rows,
            totals: body.totals,
            generatedAt: body.generatedAt,
            // The API echoes the effective flag. The toggle and the
            // caption render from this, never from what we asked for,
            // so the screen can never claim a state the numbers do not
            // actually have.
            includesAutomated: Boolean(body.includesAutomated),
          },
        };
      }
      return rejectWithValue({
        error: { message: response.data?.message || 'Failed to load metrics' },
      });
    } catch (error) {
      return rejectWithValue({
        error: { message: error.message || 'Failed to load metrics' },
      });
    }
  }
);
